import { memo, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import _ from "lodash";
import { VList } from "virtua";

import { AddIcon, RemoveIcon } from "icons";
import useComponentHeight from "utils/useComponentHeight";
import useComponentWidth from "utils/useComponentWidth";

import Button from "components/Button";
import { CircularProgress } from "components/Loading";
import Stack from "components/Stack";
import toast from "components/Toast";
import { useLoadingBackdrop } from "components/useLoadingBackdrop";

import { nGramSimilarity } from "./utils";

import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PageMatch {
  pageIndex: number;
  similarity: number;
}

export interface IPDFEmbed {
  pdfFileUrl: string | undefined;
  focusedQuoteClearanceCallback: () => void;
  focusedQuote?: string | null;
  hideZoomControls?: boolean;
}

const PDFEmbed = ({
  pdfFileUrl,
  focusedQuote,
  focusedQuoteClearanceCallback,
  hideZoomControls = false,
}: IPDFEmbed) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { height: containerHeight } = useComponentHeight(containerRef);
  const { width: containerWidth } = useComponentWidth(containerRef);

  const [scale, setScale] = useState(1.5);
  const [pdf, setPdf] = useState<pdfjs.PDFDocumentProxy | null>(null);
  const [pdfPageToTextContentMapping, setPdfPageToTextContentMapping] =
    useState<{ [key: number]: string }>({});
  const [showLoading, hideLoading] = useLoadingBackdrop();

  useEffect(() => {
    if (_.isNil(focusedQuote)) {
      return;
    }

    showLoading();

    const mostSimilarPage: PageMatch = Object.keys(
      pdfPageToTextContentMapping
    ).reduce<PageMatch>(
      (bestMatch, pageIndex) => {
        const pageText = pdfPageToTextContentMapping[parseInt(pageIndex)];
        const similarity = nGramSimilarity({
          fullText: pageText,
          searchString: focusedQuote,
        });

        return similarity > bestMatch.similarity
          ? { pageIndex: parseInt(pageIndex), similarity }
          : bestMatch;
      },
      { pageIndex: 0, similarity: 0 }
    );

    const errorMessage =
      "We're sorry but we couldn't find the source page you're looking for.";

    const anyPage = document.querySelector(".react-pdf__Page__canvas");

    if (anyPage === null) {
      toast.errorMessage(errorMessage);
      focusedQuoteClearanceCallback();
      hideLoading();
      return;
    }

    const pageElement = document.querySelector(
      `[data-page-number="${mostSimilarPage.pageIndex}"]`
    );

    if (pageElement === null) {
      toast.errorMessage(errorMessage);
      focusedQuoteClearanceCallback();
      hideLoading();
      return;
    }

    pageElement.scrollIntoView({
      block: "center",
    });
    pageElement.classList.add("highlighted");

    setTimeout(() => {
      pageElement.classList.remove("highlighted");
    }, 2000);

    hideLoading();
    focusedQuoteClearanceCallback();
  }, [
    pdfPageToTextContentMapping,
    focusedQuote,
    showLoading,
    hideLoading,
    focusedQuoteClearanceCallback,
  ]);

  useEffect(() => {
    if (_.isNil(pdf) || pdf.numPages === 0) {
      return;
    }

    Promise.all(
      _.range(1, pdf.numPages + 1).map((pageNumber) => pdf.getPage(pageNumber))
    ).then((pages) =>
      Promise.all(pages.map((page) => page.getTextContent())).then(
        (textContents) =>
          textContents.map((textContent, index) =>
            setPdfPageToTextContentMapping((prevState) => ({
              ...prevState,
              [index + 1]: textContent.items
                .map((item) => _.get(item, "str", ""))
                .join(" "),
            }))
          )
      )
    );
  }, [pdf]);

  return (
    <Stack
      sx={{
        alignItems: "center",
        height: "100%",
        position: "relative",
        overflowY: "hidden",
        "&:hover .zoomControls": {
          opacity: 1,
        },
        ".react-pdf__Document": {
          height: containerHeight,
          width: containerWidth,
        },
        "canvas.react-pdf__Page__canvas": {
          width: "100% !important",
          height: "auto !important",
        },
        ".textLayer": {
          pointerEvents: "none",
          display: "none",
        },
        ".highlighted": {
          animationDelay: "350ms",
          animation: "zoomInAndOut 1s linear",
          "@keyframes zoomInAndOut": {
            "0%": {
              transform: "scale(1)",
            },
            "50%": {
              transform: "scale(1.1)",
            },
            "100%": {
              transform: "scale(1)",
            },
          },
        },
      }}
      ref={containerRef}
    >
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "absolute",
        }}
      >
        <CircularProgress />
      </div>

      {!_.isNil(pdfFileUrl) && (
        <>
          {!hideZoomControls && (
            <Stack
              direction="row"
              className="zoomControls"
              sx={{
                top: "10px",
                marginBottom: "-50px",
                position: "sticky",
                justifyContent: "center",
                zIndex: 3,
                opacity: 0,
                width: containerRef.current?.clientWidth,
                transition: "opacity 0.2s",
              }}
            >
              <Button
                size="small"
                title="Zoom out"
                variant="outlined"
                onClick={() => setScale((prevScale) => prevScale - 0.1)}
                sx={{ height: "50px" }}
              >
                <RemoveIcon sx={{ width: "40px", height: "40px" }} />
              </Button>
              <Button
                size="small"
                title="Zoom in"
                variant="outlined"
                onClick={() => setScale((prevScale) => prevScale + 0.1)}
                sx={{ height: "50px" }}
              >
                <AddIcon sx={{ width: "40px", height: "40px" }} />
              </Button>
            </Stack>
          )}
          <Document
            file={pdfFileUrl}
            loading={null}
            onLoadSuccess={(pdf) => setPdf(pdf)}
          >
            <VList style={{ height: containerHeight }} overscan={100}>
              {!_.isNil(pdf) &&
                pdf.numPages >= 1 &&
                _.range(1, pdf.numPages + 1).map((pageNumber) => (
                  <Page
                    loading={null}
                    key={pageNumber}
                    pageNumber={pageNumber}
                    renderAnnotationLayer={false}
                    scale={scale}
                  />
                ))}
            </VList>
          </Document>
        </>
      )}
    </Stack>
  );
};

export default memo(
  PDFEmbed,
  (prevProps, nextProps) =>
    prevProps.pdfFileUrl === nextProps.pdfFileUrl &&
    prevProps.focusedQuote === nextProps.focusedQuote
);
